import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ConfigProvider, Spin, theme } from 'antd';
import zhTW from 'antd/locale/zh_TW';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const Historical = lazy(() => import('./pages/Historical'));
const WithdrawalHistory = lazy(() => import('./pages/WithdrawalHistory'));
const BinanceTransfer = lazy(() => import('./pages/BinanceTransfer'));
const ProfitReport = lazy(() => import('./pages/ProfitReport'));
const ProfitCycleEditor = lazy(() => import('./pages/ProfitCycleEditor'));
const Config = lazy(() => import('./pages/Config'));
const Login = lazy(() => import('./pages/Login'));
const LandingPage = lazy(() => import('./pages/LandingPage'));

const AnalyticsTracker = () => {
  const location = useLocation();

  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('config', 'G-GN4MSC3CGQ', {
        page_path: location.pathname + location.search,
      });
    }
  }, [location]);

  return null;
};

const PageFallback = () => (
  <div style={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
    <Spin size="large" />
  </div>
);

const App = () => {
  return (
    <ConfigProvider
      locale={zhTW}
      theme={{
        algorithm: theme.darkAlgorithm,
        token: {
          colorPrimary: '#00A3FF',
          colorSuccess: '#00D27A',
          colorError: '#FF4D4F',
          colorWarning: '#FFC700',
          borderRadius: 8,
          fontFamily: "'Inter', 'Noto Sans TC', sans-serif",
        },
      }}
    >
      <ErrorBoundary>
        <AuthProvider>
          <Router>
            <AnalyticsTracker />
            <Suspense fallback={<PageFallback />}>
              <Routes>
                {/* 公開頁面 */}
                <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<Login />} />
              
              {/* 受保護的 Dashboard 區域 */}
              <Route path="/dashboard/*" element={
                <ProtectedRoute>
                  <Layout>
                    <ErrorBoundary>
                      <Routes>
                        <Route path="/" element={<Dashboard />} />
                        <Route path="/historical" element={<Historical />} />
                        <Route path="/withdrawals" element={<WithdrawalHistory />} />
                        <Route path="/binance-transfer" element={<BinanceTransfer />} />
                        <Route path="/profit-report" element={<ProfitReport />} />
                        <Route path="/profit-cycles/new" element={<ProfitCycleEditor />} />
                        <Route path="/profit-cycles/:cycleDate/edit" element={<ProfitCycleEditor />} />
                        <Route path="/config" element={<Config />} />
                      </Routes>
                    </ErrorBoundary>
                  </Layout>
                </ProtectedRoute>
              } />
              </Routes>
            </Suspense>
          </Router>
        </AuthProvider>
      </ErrorBoundary>
    </ConfigProvider>
  );
};

export default App;
